// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/pages/JournalViewPage.js
//
// MODULE:
// Finance & Accounts Management System
//
// FEATURE:
// Manual Journal - View
//
// PURPOSE:
// Read-only display of an existing journal.
//
// ARCHITECTURE:
// - React functional component
// - Existing FinancePageHeader reused
// - Existing JournalTotals reused
// - Existing journalService reused
// - No Redux
// - Existing APIs only
// ======================================================================

import React, {
    useEffect,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

// ======================================================================
// FINANCE COMMON COMPONENT
// ======================================================================

import FinancePageHeader
    from "../../../components/FinancePageHeader";

// ======================================================================
// JOURNAL COMPONENT
// ======================================================================

import JournalTotals
    from "../../../components/JournalTotals";

// ======================================================================
// JOURNAL SERVICE
// ======================================================================

import journalService
    from "../../../services/journalService";

// ======================================================================
// COMPONENT
// ======================================================================

export default function JournalViewPage() {

    const navigate =
        useNavigate();

    const {
        id,
    } = useParams();

    // ==================================================================
    // STATE
    // ==================================================================

    const [
        journal,
        setJournal
    ] = useState(null);

    const [
        lines,
        setLines
    ] = useState([]);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        errorMessage,
        setErrorMessage
    ] = useState("");

    // ==================================================================
    // LOAD JOURNAL
    // ==================================================================

    useEffect(() => {

        let mounted = true;

        async function loadJournal() {

            try {

                setLoading(true);

                setErrorMessage("");

                // ------------------------------------------------------
                // Load header and lines using existing APIs.
                // ------------------------------------------------------

                const [
                    header,
                    journalLines,
                ] = await Promise.all([
                    journalService.getJournal(id),
                    journalService.listJournalLines(id),
                ]);

                if (!mounted) {
                    return;
                }

                setJournal(
                    header || null
                );

                setLines(
                    Array.isArray(
                        journalLines
                    )
                        ? journalLines
                        : []
                );

            } catch (error) {

                console.error(
                    "Failed to load journal:",
                    error
                );

                if (mounted) {

                    setErrorMessage(
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to load journal."
                    );

                }

            } finally {

                if (mounted) {
                    setLoading(false);
                }

            }
        }

        loadJournal();

        return () => {
            mounted = false;
        };

    }, [id]);

    // ==================================================================
    // FORMAT AMOUNT
    // ==================================================================

    const formatAmount = (
        value
    ) => {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );
    };

    // ==================================================================
    // FORMAT DATE
    // ==================================================================

    const formatDate = (
        value
    ) => {

        if (!value) {
            return "-";
        }

        return String(
            value
        ).slice(
            0,
            10
        );
    };

    // ==================================================================
    // HEADER
    // ==================================================================

    return (
        <div className="container-fluid">

            <FinancePageHeader
                icon="fas fa-file-invoice"
                title="View Journal"
                subtitle={
                    journal?.journal_no
                        ? `Journal ${journal.journal_no}`
                        : "Journal details"
                }
                breadcrumbs={[
                    {
                        label: "Finance",
                        path: "/finance",
                    },
                    {
                        label: "Manual Journal",
                        path: "/finance/manual-journal",
                    },
                    {
                        label: "View Journal",
                    },
                ]}
                actions={

                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() =>
                            navigate(
                                "/finance/manual-journal"
                            )
                        }
                    >
                        <i className="fas fa-arrow-left me-1" />
                        Back
                    </button>

                }
            />

            {/* ==========================================================
                ERROR
            ========================================================== */}

            {errorMessage && (

                <div className="alert alert-danger">
                    {errorMessage}
                </div>

            )}

            {/* ==========================================================
                LOADING
            ========================================================== */}

            {loading ? (

                <div className="card border-0 shadow-sm">

                    <div className="card-body text-center py-5">

                        <div
                            className="spinner-border text-primary"
                            role="status"
                        />

                        <div className="mt-3">
                            Loading journal...
                        </div>

                    </div>

                </div>

            ) : journal ? (

                <>
                    {/* ==================================================
                        JOURNAL HEADER
                    ================================================== */}

                    <div className="card shadow-sm border-0 mb-4">

                        <div className="card-header bg-white border-bottom">

                            <div className="fw-semibold">
                                Journal Header
                            </div>

                        </div>

                        <div className="card-body">

                            <div className="row g-3">

                                {/* --------------------------------------
                                    JOURNAL NUMBER
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Journal No.
                                    </label>

                                    <div className="fw-semibold">
                                        {
                                            journal.journal_no ||
                                            "-"
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    COMPANY
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Company
                                    </label>

                                    <div>
                                        {
                                            journal.company_name ||
                                            journal.company_id ||
                                            "-"
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    FINANCIAL YEAR
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Financial Year
                                    </label>

                                    <div>
                                        {
                                            journal.year_name ||
                                            journal.financial_year_name ||
                                            journal.financial_year_id ||
                                            "-"
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    FINANCIAL PERIOD
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Financial Period
                                    </label>

                                    <div>
                                        {
                                            journal.period_name ||
                                            journal.financial_period_name ||
                                            journal.financial_period_id ||
                                            "-"
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    JOURNAL DATE
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Journal Date
                                    </label>

                                    <div>
                                        {
                                            formatDate(
                                                journal.journal_date
                                            )
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    JOURNAL TYPE
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Journal Type
                                    </label>

                                    <div>
                                        {
                                            journal.journal_type ||
                                            "-"
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    CURRENCY
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Currency
                                    </label>

                                    <div>
                                        {
                                            journal.currency_code ||
                                            journal.currency_name ||
                                            journal.currency_id ||
                                            "-"
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    EXCHANGE RATE
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Exchange Rate
                                    </label>

                                    <div>
                                        {
                                            journal.exchange_rate ??
                                            "-"
                                        }
                                    </div>

                                </div>

                                {/* --------------------------------------
                                    STATUS
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Status
                                    </label>

                                    <div>

                                        <span className="badge bg-secondary">

                                            {
                                                journal.status ||
                                                "-"
                                            }

                                        </span>

                                    </div>

                                </div>

                                {/* --------------------------------------
                                    APPROVAL STATUS
                                -------------------------------------- */}

                                <div className="col-md-3">

                                    <label className="form-label text-muted">
                                        Approval Status
                                    </label>

                                    <div>

                                        <span className="badge bg-secondary">

                                            {
                                                journal.approval_status ||
                                                "-"
                                            }

                                        </span>

                                    </div>

                                </div>

                                {/* --------------------------------------
                                    NARRATION
                                -------------------------------------- */}

                                <div className="col-12">

                                    <label className="form-label text-muted">
                                        Narration
                                    </label>

                                    <div className="border rounded p-2 bg-light">

                                        {
                                            journal.remarks ||
                                            "-"
                                        }

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                    {/* ==================================================
                        JOURNAL LINES
                    ================================================== */}

                    <div className="card shadow-sm border-0 mb-4">

                        <div className="card-header bg-white border-bottom">

                            <div className="fw-semibold">
                                Journal Lines
                            </div>

                        </div>

                        <div className="card-body p-0">

                            <div className="table-responsive">

                                <table className="table table-bordered table-hover mb-0">

                                    <thead className="table-light">

                                        <tr>

                                            <th>
                                                Line
                                            </th>

                                            <th>
                                                Account
                                            </th>

                                            <th>
                                                Cost Center
                                            </th>

                                            <th>
                                                Description
                                            </th>

                                            <th className="text-end">
                                                Debit
                                            </th>

                                            <th className="text-end">
                                                Credit
                                            </th>

                                        </tr>

                                    </thead>

                                    <tbody>

                                        {lines.length === 0 ? (

                                            <tr>

                                                <td
                                                    colSpan="6"
                                                    className="text-center text-muted py-4"
                                                >
                                                    No journal lines found.
                                                </td>

                                            </tr>

                                        ) : (

                                            lines.map(
                                                (
                                                    line
                                                ) => (

                                                    <tr
                                                        key={
                                                            line.id
                                                        }
                                                    >

                                                        <td>
                                                            {
                                                                line.line_no ||
                                                                "-"
                                                            }
                                                        </td>

                                                        <td>
                                                            {
                                                                line.account_name ||
                                                                line.account_code ||
                                                                line.account_id ||
                                                                line.coa_id ||
                                                                "-"
                                                            }
                                                        </td>

                                                        <td>
                                                            {
                                                                line.cost_center_name ||
                                                                line.cost_center_code ||
                                                                line.cost_center_id ||
                                                                "-"
                                                            }
                                                        </td>

                                                        <td>
                                                            {
                                                                line.description ||
                                                                "-"
                                                            }
                                                        </td>

                                                        <td className="text-end">

                                                            {
                                                                formatAmount(
                                                                    line.debit_amount
                                                                )
                                                            }

                                                        </td>

                                                        <td className="text-end">

                                                            {
                                                                formatAmount(
                                                                    line.credit_amount
                                                                )
                                                            }

                                                        </td>

                                                    </tr>

                                                )
                                            )

                                        )}

                                    </tbody>

                                </table>

                            </div>

                        </div>

                    </div>

                    {/* ==================================================
                        TOTALS
                    ================================================== */}

                    <JournalTotals
                        lines={lines}
                        narration={
                            journal.remarks || ""
                        }
                    />

                </>

            ) : (

                <div className="alert alert-warning">
                    Journal not found.
                </div>

            )}

        </div>
    );
}